import React from "react";
import { Text, View } from "react-native";
import tw from "twrnc";

import Button from "@/components/settings/Button";
import { colors, text } from "@/components/settings/theme";
import { t, tn } from "@/localization";
import {
  SLOW_AFTER_MS,
  formatBytes,
  recordingMeta,
  recordingTitle,
} from "./logic";

// One recording in Download everything: title, date and length on the left,
// its download button on the right, and what's happening under the title.
//
//   Download          nothing asked yet (or the link is ready again)
//   Preparing…        evt.live is joining the parts into one MP4
//   Download + Ready  the MP4 was prepared while you waited
//   Restore + Delete  archived (the Secure Archive): never downloaded from
//                     here; Restore opens the paid restore (evt.live
//                     /api/payRestore, as the events list's Restore does)
//   Try again         the last attempt failed

function statusLine(rec, state, now) {
  switch (state?.status) {
    case "preparing": {
      const slow = state.startedAt && now - state.startedAt > SLOW_AFTER_MS;
      return {
        tone: "muted",
        text: slow
          ? t(
              "Still preparing. Long recordings take a while; you can leave this page and come back."
            )
          : rec.parts > 1
          ? t(
              "Joining {parts} parts into one MP4. This usually takes a few minutes.",
              { parts: rec.parts }
            )
          : t("Preparing the MP4. This usually takes a few minutes."),
      };
    }
    case "ready":
      return state.prepared
        ? {
            tone: "success",
            text: [t("Ready to download"), formatBytes(state.sizeBytes)]
              .filter(Boolean)
              .join(" · "),
          }
        : null;
    case "archived":
      return archivedLine(rec, state);
    case "failed":
      return {
        tone: "error",
        text: state.gaveUp
          ? t(
              "This is taking much longer than usual. Try again, or contact support."
            )
          : state.error || t("Couldn't prepare the download. Try again."),
      };
    default:
      return rec.archived ? archivedLine(rec, state) : null;
  }
}

const archivedOf = (rec, state) => rec.archived || state?.status === "archived";
const restoreUrlOf = (rec, state) =>
  rec.restoreUrl || state?.restoreUrl || null;

const archivedLine = (rec, state) => ({
  tone: "muted",
  text: restoreUrlOf(rec, state)
    ? t(
        "In the Secure Archive, so it can't be downloaded. Restore it to download it again, or delete it."
      )
    : t("In the Secure Archive, so it can't be downloaded. You can delete it."),
});

const TONE_STYLE = {
  muted: text.hint,
  success: [text.hint, { color: colors.success }],
  error: text.error,
};

function ActionButton({
  rec,
  state,
  deleting,
  onDownload,
  onDelete,
  onRestore,
}) {
  const common = { size: "sm", fullWidth: false };
  const label = recordingTitle(rec);
  const restoreUrl = restoreUrlOf(rec, state);
  if (archivedOf(rec, state) && restoreUrl)
    return (
      <Button
        {...common}
        text={t("Restore")}
        icon="refresh-outline"
        accessibilityLabel={t("Restore {title}", { title: label })}
        onPress={() => onRestore(rec, restoreUrl)}
      />
    );
  if (archivedOf(rec, state))
    return (
      <Button
        {...common}
        variant="danger"
        text={t("Delete")}
        icon="trash-outline"
        accessibilityLabel={t("Delete {title}", { title: label })}
        loading={!!deleting}
        onPress={() => onDelete(rec)}
      />
    );
  switch (state?.status) {
    case "checking":
      return <Button {...common} text={t("Download")} loading />;
    case "preparing":
      return (
        <Button
          {...common}
          text={t("Preparing…")}
          loading
          accessibilityLabel={t("Preparing {title}", { title: label })}
        />
      );
    case "failed":
      return (
        <Button
          {...common}
          text={t("Try again")}
          icon="refresh"
          accessibilityLabel={t("Try again: {title}", { title: label })}
          onPress={() => onDownload(rec)}
        />
      );
    default:
      return (
        <Button
          {...common}
          text={t("Download")}
          icon="download-outline"
          accessibilityLabel={t("Download {title}", { title: label })}
          onPress={() => onDownload(rec)}
        />
      );
  }
}

const RecordingRow = ({
  rec,
  state,
  pdfState,
  now = Date.now(),
  deleting,
  onDownload,
  onDelete,
  onRestore,
  onGuestbookPdf,
  first,
}) => {
  const meta = [recordingMeta(rec), formatBytes(rec.sizeBytes)]
    .filter(Boolean)
    .join(" · ");
  const status = statusLine(rec, state, now);
  return (
    <View
      testID={`recording-${rec.slug}`}
      style={[
        tw`py-3`,
        {
          borderBottomWidth: 1,
          borderBottomColor: colors.divider,
        },
        first && { borderTopWidth: 1, borderTopColor: colors.divider },
      ]}
    >
      <View style={tw`flex-row items-start`}>
        <View style={tw`flex-1 pr-3`}>
          <Text style={text.bodyStrong} numberOfLines={2}>
            {recordingTitle(rec)}
          </Text>
          {!!meta && <Text style={[text.hint, tw`mt-0.5`]}>{meta}</Text>}
        </View>
        <ActionButton
          rec={rec}
          state={state}
          deleting={deleting}
          onDownload={onDownload}
          onDelete={onDelete}
          onRestore={onRestore}
        />
      </View>
      {!!status && (
        <Text
          accessibilityLiveRegion="polite"
          style={[TONE_STYLE[status.tone], tw`mt-1.5`]}
        >
          {status.text}
        </Text>
      )}
      {archivedOf(rec, state) && !!restoreUrlOf(rec, state) && (
        <Button
          variant="danger-text"
          size="sm"
          fullWidth={false}
          icon="trash-outline"
          text={t("Delete")}
          accessibilityLabel={t("Delete {title}", {
            title: recordingTitle(rec),
          })}
          loading={!!deleting}
          onPress={() => onDelete(rec)}
          style={tw`mt-1 -ml-2`}
        />
      )}
      {rec.guestbookMessages > 0 && !archivedOf(rec, state) && (
        <Button
          variant="tertiary"
          size="sm"
          fullWidth={false}
          icon="book-outline"
          text={tn(
            "Guestbook PDF · {count} message",
            "Guestbook PDF · {count} messages",
            rec.guestbookMessages
          )}
          accessibilityLabel={t("Download the guestbook of {title} as a PDF", {
            title: recordingTitle(rec),
          })}
          loading={pdfState?.status === "checking"}
          onPress={() => onGuestbookPdf(rec)}
          style={tw`mt-1 -ml-2`}
        />
      )}
    </View>
  );
};

export default React.memo(RecordingRow);
